import { useWikiSpaces } from "@/hooks/use-wiki-spaces";
import { AddWikiSpaceModal } from "@/modals/wiki/add-wiki-space-modal";
import { BlankSlate } from "@msuite/ui/blank-slate";
import { ContentCard } from "@msuite/ui/content-card";
import { useDisclosure } from "@msuite/ui/hooks";
import { icon } from "@msuite/ui/icons";
import { useAutoAnimate } from "@msuite/ui/libs/auto-animate";
import { useTranslation } from "@msuite/ui/localization";
import { array } from "@msuite/utils/array";
import type { Nullable } from "@msuite/utils/types";
import React from "react";
import { useNavigate } from "react-router";
import { WikiSpaceItem } from "./wiki-space-item";

/** Props Interface */
interface WikiSidebarProps {
	spaceId: Nullable<string>;
	pageId: Nullable<string>;
}

const _arr = array(20);

export function WikiSidebar({ spaceId, pageId }: WikiSidebarProps) {
	/** Hooks */
	const { t } = useTranslation();
	const navigate = useNavigate();
	const addSpaceModal = useDisclosure();

	/** Query */
	const { wikiSpaces, isLoading } = useWikiSpaces();
	const [parent] = useAutoAnimate();

	/** Computed */
	const hasSpaces = (wikiSpaces ?? []).length > 0;

	/** Render */
	return (
		<React.Fragment>
			<div className="flex-1">
				<ContentCard
					title={t("wiki.spaces")}
					icon={icon("BookOpen")}
					identifier={spaceId || "wiki-sidebar"}
					variant="plain"
					dataPageBreadcrumbsTitle={
						wikiSpaces?.find((i) => i.id === spaceId)?.name || t("wiki.spaces")
					}
					menuItems={[
						{
							icon: icon("Plus"),
							label: t("wiki.add_space"),
							onClick: addSpaceModal.onOpen,
						},
					]}
				>
					{hasSpaces ? (
						<div
							className="flex flex-col gap-1"
							ref={parent}
						>
							{wikiSpaces?.map((space) => (
								<WikiSpaceItem
									key={space.id}
									space={space}
									isActive={space.id === spaceId}
									activePageId={pageId}
								/>
							))}
						</div>
					) : (
						!isLoading && (
							<BlankSlate
								label={t("wiki.no_spaces_available")}
								Icon={icon("BookOpen")}
								onClick={addSpaceModal.onOpen}
							/>
						)
					)}
				</ContentCard>
			</div>
			{addSpaceModal.isOpen && (
				<AddWikiSpaceModal
					isOpen={addSpaceModal.isOpen}
					onClose={addSpaceModal.onClose}
					onComplete={(newSpaceId) => navigate(`/wiki/${newSpaceId}`)}
				/>
			)}
		</React.Fragment>
	);
}
